import React, { useState } from "react";
import { useParams } from "react-router-dom";
import useApiEp from "../../hooks/useApiEp";
import {
  Container,
  TextField,
  Button,
  Typography,
  Alert,
  MenuItem,
  Box,
} from "@mui/material";
import BackBtn from "../../widgets/BackBtn";
import Joi, { ref } from "joi";
import PageTitle from "../../widgets/PageTitle";
import { LoadingButton } from "@mui/lab";
import { API_ENDPOINT } from "../../utils/constants";
import { getHeader, toastError } from "../../utils/util";
import axios from "axios";
import { toast } from "react-toastify";
// Joi schema for validation
const schema = Joi.object({
  status: Joi.string()
    .valid("active", "inactive", "suspended")
    .required()
    .messages({
      "string.base": "Status must be a string",
      "any.only": "Status must be either 'active' or 'inactive'",
      "any.required": "Status is required",
    }),
  phoneNumberId: Joi.string()
    .pattern(/^[0-9]+$/)
    .required()
    .messages({
      "string.pattern.base": "Phone Number ID must contain only digits",
      "any.required": "Phone Number ID is required",
    }),
  wabaToken: Joi.string().min(10).required().messages({
    "string.min": "WABA Token must be at least 10 characters long",
    "any.required": "WABA Token is required",
  }),
  wabaAccountId: Joi.string()
    .pattern(/^[0-9]+$/)
    .required()
    .messages({
      "string.pattern.base": "WABA Account ID must contain only digits",
      "any.required": "WABA Account ID is required",
    }),
  appId: Joi.string()
    .pattern(/^[0-9]+$/)
    .required()
    .messages({
      "string.pattern.base": "App ID must contain only digits",
      "any.required": "App ID is required",
    }),
  rcsId: Joi.string()
    .pattern(/^[a-zA-Z0-9]+$/) // only alphanumeric if provided
    .allow("") // allow empty string
    .optional() // field is optional
    .messages({
      "string.pattern.base": "RCS ID must contain only letters and digits",
    }),
  rcsRate: Joi.number().min(0).default(0).messages({
    "number.base": "RCS Rate must be a number",
    "number.min": "RCS Rate must be at least 0",
  }),
  sendEp: Joi.string()
    .max(100)
    .uri({ scheme: ["http", "https"] })
    .required()
    .messages({
      "string.uri": "sendEp must be a valid URL (http or https)",
      "string.uriCustomScheme": "sendEp must be a valid URL (http or https)",
      "any.required": "sendEp is required",
      "string.max": "sendEp must not exceed 100 characters",
    }),
  sessionEp: Joi.string()
    .max(100)
    .uri({ scheme: ["http", "https"] })
    .required()
    .messages({
      "string.uri": "sessionEp must be a valid URL (http or https)",
      "string.uriCustomScheme": "sessionEp must be a valid URL (http or https)",
      "any.required": "sessionEp is required",
      "string.max": "sessionEp must not exceed 100 characters",
    }),
});

const EditAccount = () => {
  const { id } = useParams();
  const { data, isLoading, isError, error, refetch } = useApiEp(
    `/root/get-account/${id}`
  );

  // State for form data and validation errors
  const [formData, setFormData] = useState({
    status: "",
    plan: "",
    phoneNumberId: "",
    wabaToken: "",
    wabaAccountId: "",
    appId: "",
    rcsId: "",
    rcsRate: 0,
    sendEp: "",
    sessionEp: "",
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Initialize form data when API data is loaded
  React.useEffect(() => {
    if (data) {
      setFormData({
        status: data.status || "",
        phoneNumberId: data.phoneNumberId || "",
        wabaToken: data.wabaToken || "",
        wabaAccountId: data.wabaAccountId || "",
        appId: data.appId || "",
        rcsId: data.rcsId || "",
        rcsRate: data.rcsRate || 0,
        sendEp: data?.apiEp?.campaign || "",
        sessionEp: data?.apiEp?.session || "",
      });
    }
  }, [data]);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "rcsRate") {
      setFormData((prev) => ({
        ...prev,
        [name]: value === "" ? 0 : parseFloat(value) || 0,
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    // Clear validation error for the field when user types
    setValidationErrors((prev) => ({ ...prev, [name]: null }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationErrors({});
    setSubmitError(null);

    // Validate form data
    const { error } = schema.validate(formData, { abortEarly: false });
    if (error) {
      const errors = error.details.reduce((acc, err) => {
        acc[err.path[0]] = err.message;
        return acc;
      }, {});
      setValidationErrors(errors);
      return;
    }
    const payload = {
      ...formData,
      wallet: formData.wallet === "" ? null : formData.wallet,
    };
    try {
      // Replace with your actual API call to update the account
      setSubmitting(true);
      const response = await axios.put(
        `${API_ENDPOINT}/root/update-account/${id}`,
        payload,
        getHeader()
      );
      toast.success(response.data.message);
      refetch();
    } catch (err) {
      toastError(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) return <Typography>Loading...</Typography>;
  if (isError) return <Typography color="error">{error.message}</Typography>;

  return (
    <Container sx={{ mt: 3 }} maxWidth="md">
      <BackBtn />
      <PageTitle>Edit Account</PageTitle>
      {submitError && <Alert severity="error">{submitError}</Alert>}
      <form style={{ marginTop: "16px" }} onSubmit={handleSubmit}>
        <TextField
          select
          label="Status"
          name="status"
          value={formData.status}
          onChange={handleChange}
          fullWidth
          required
          size="small"
          sx={{ mb: 1 }}
          error={!!validationErrors.status}
          helperText={validationErrors.status}
        >
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="inactive">Inactive</MenuItem>
          <MenuItem value="suspended">Suspended</MenuItem>
        </TextField>
        <TextField
          fullWidth
          size="small"
          label="Phone Number ID"
          name="phoneNumberId"
          value={formData.phoneNumberId}
          onChange={handleChange}
          error={!!validationErrors.phoneNumberId}
          helperText={validationErrors.phoneNumberId}
          margin="normal"
          required
        />
        <TextField
          fullWidth
          size="small"
          label="WABA Token"
          name="wabaToken"
          multiline
          rows={3}
          value={formData.wabaToken}
          onChange={handleChange}
          error={!!validationErrors.wabaToken}
          helperText={validationErrors.wabaToken}
          margin="normal"
          required
        />
        <TextField
          fullWidth
          size="small"
          label="WABA Account ID"
          name="wabaAccountId"
          value={formData.wabaAccountId}
          onChange={handleChange}
          error={!!validationErrors.wabaAccountId}
          helperText={validationErrors.wabaAccountId}
          margin="normal"
          required
        />
        <TextField
          fullWidth
          label="App ID"
          name="appId"
          size="small"
          value={formData.appId}
          onChange={handleChange}
          error={!!validationErrors.appId}
          helperText={validationErrors.appId}
          margin="normal"
          required
        />
        <TextField
          fullWidth
          label="RCS Id"
          name="rcsId"
          size="small"
          value={formData.rcsId}
          onChange={handleChange}
          error={!!validationErrors.rcsId}
          helperText={validationErrors.rcsId}
          margin="normal"
        />
        {/* <TextField
          fullWidth
          label="RCS Rate(in paise)"
          name="rcsRate"
          type="number"
          size="small"
          value={formData.rcsRate}
          onChange={handleChange}
          error={!!validationErrors.rcsRate}
          helperText={validationErrors.rcsRate}
          margin="normal"
          inputProps={{ step: "0.01", min: "0" }}
        /> */}
        <TextField
          fullWidth
          label="Campaign Endpoint"
          name="sendEp"
          size="small"
          value={formData.sendEp}
          onChange={handleChange}
          error={!!validationErrors.sendEp}
          helperText={validationErrors.sendEp}
          margin="normal"
          required
        />
        <TextField
          fullWidth
          label="Session Endpoint"
          name="sessionEp"
          size="small"
          value={formData.sessionEp}
          onChange={handleChange}
          error={!!validationErrors.sessionEp}
          helperText={validationErrors.sessionEp}
          margin="normal"
          required
        />

        <Box display={"flex"} justifyContent={"end"}>
          <LoadingButton
            type="submit"
            variant="contained"
            color="primary"
            loading={submitting}
            sx={{ mt: 2 }}
          >
            Save Changes
          </LoadingButton>
        </Box>
      </form>
    </Container>
  );
};

export default EditAccount;
